'use client';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { REGEXP_ONLY_DIGITS } from 'input-otp';
import { Sparkles, Loader2, MessageSquareText, ArrowRight, CheckCircle2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import z from 'zod';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { showToast } from '@/lib/toast';
import { handleApiErrors } from '@/lib/api-error-utils';
import { cn } from '@/lib/utils';

const SUBSCRIBE_QUERY_PARAM = 'subscribe_to_daily_scripture';

const FIXED_SMS_VIDEO_CATEGORY_ID = '69147b1aec4fb532e9cea29a';

const US_TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Phoenix',
  'America/Anchorage',
  'Pacific/Honolulu',
  'America/Puerto_Rico',
  'Pacific/Guam',
  'Pacific/Pago_Pago',
];

const SubscribeSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  phoneNumber: z.string().min(10, 'Enter a valid phone number'),
  countryCode: z.string().min(1, 'Country code is required'),
  timezone: z.string().min(1, 'Timezone is required'),
  smsConsent: z.literal(true, {
    errorMap: () => ({ message: 'You must agree to receive text messages' }),
  }),
});

const VerifySchema = z.object({
  code: z
    .string()
    .min(1, 'Verification code is required')
    .regex(/^\d{6}$/, 'Verification code must be 6 digits'),
});

function parsePhone(fullPhone, countryCode) {
  const rawNumber = fullPhone?.replace(/\s+/g, '').replace(/^\+/, '') || '';
  const rawCode = countryCode?.replace(/^\+/, '') || '';
  const localNumber = rawNumber.startsWith(rawCode) ? rawNumber.slice(rawCode.length) : rawNumber;

  return { countryCode: rawCode, phoneNumber: localNumber };
}

function getBrowserTimezone() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz && US_TIMEZONES.includes(tz)) return tz;
  } catch {
    // fall through
  }
  return 'America/New_York';
}

function getTimezoneOptions() {
  try {
    if (typeof Intl !== 'undefined' && typeof Intl.supportedValuesOf === 'function') {
      const supported = new Set(Intl.supportedValuesOf('timeZone'));
      const usZones = US_TIMEZONES.filter((tz) => supported.has(tz));
      if (usZones.length) return usZones;
    }
  } catch {
    // fall through
  }
  return US_TIMEZONES;
}

export default function InspirationalSmsDialogue({
  triggerClassName,
  triggerLabel = 'Daily Scripture',
  variant = 'default',
  children,
}) {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('subscribe');
  const [subscribeStep, setSubscribeStep] = useState('form');
  const [phonePayload, setPhonePayload] = useState({ countryCode: '', phoneNumber: '' });
  const [isResending, setIsResending] = useState(false);
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();

  const timezoneOptions = useMemo(() => getTimezoneOptions(), []);

  useEffect(() => {
    if (searchParams.get(SUBSCRIBE_QUERY_PARAM) !== 'true') return;

    setOpen(true);

    const nextParams = new URLSearchParams(searchParams.toString());
    nextParams.delete(SUBSCRIBE_QUERY_PARAM);
    const query = nextParams.toString();
    router.replace(query ? `${pathname}?${query}` : pathname, { scroll: false });
  }, [searchParams, pathname, router]);

  const subscribeForm = useForm({
    resolver: zodResolver(SubscribeSchema),
    defaultValues: {
      name: '',
      phoneNumber: '',
      countryCode: '1',
      timezone: getBrowserTimezone(),
      smsConsent: false,
    },
  });

  const verifyForm = useForm({
    resolver: zodResolver(VerifySchema),
    defaultValues: { code: '' },
  });

  const resetDialog = () => {
    setActiveTab('subscribe');
    setSubscribeStep('form');
    setPhonePayload({ countryCode: '', phoneNumber: '' });
    subscribeForm.reset({
      name: '',
      phoneNumber: '',
      countryCode: '1',
      timezone: getBrowserTimezone(),
      smsConsent: false,
    });
    verifyForm.reset({ code: '' });
  };

  const handleOpenChange = (nextOpen) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      resetDialog();
    }
  };

  const onSubscribe = async (formData) => {
    const { countryCode, phoneNumber } = parsePhone(formData.phoneNumber, formData.countryCode);

    if (!/^\d{7,15}$/.test(phoneNumber)) {
      subscribeForm.setError('phoneNumber', {
        message: 'Phone number must be 7–15 digits',
      });
      return;
    }

    const payload = {
      name: formData.name,
      countryCode,
      phoneNumber,
      videoCategory: FIXED_SMS_VIDEO_CATEGORY_ID,
      timezone: formData.timezone,
    };

    try {
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}/inspirational-messages/subscribe`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );

      const data = await response.json();

      if (response.ok) {
        setPhonePayload({ countryCode, phoneNumber });
        setSubscribeStep('verify');
        showToast(data?.message || 'Verification code sent', 'success');
      } else if (response.status === 400 || response.status === 409) {
        handleApiErrors(subscribeForm, data.errors);
        if (data?.message) {
          showToast(data.message, 'error');
        }
      } else {
        showToast(data?.message || 'Something went wrong', 'error');
      }
    } catch (error) {
      showToast(error?.message || 'Something went wrong', 'error');
    }
  };

  const onVerify = async (formData) => {
    try {
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}/inspirational-messages/verify`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            countryCode: phonePayload.countryCode,
            phoneNumber: phonePayload.phoneNumber,
            code: formData.code,
          }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        setSubscribeStep('success');
        showToast(data?.message || 'Phone verified successfully', 'success');
      } else if (response.status === 400 || response.status === 409) {
        handleApiErrors(verifyForm, data.errors);
      } else {
        showToast(data?.message || 'Verification failed', 'error');
      }
    } catch (error) {
      showToast(error?.message || 'Something went wrong', 'error');
    }
  };

  const handleResendOtp = async () => {
    setIsResending(true);
    try {
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}/inspirational-messages/resend-otp`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(phonePayload),
        }
      );

      const data = await response.json();
      showToast(data?.message || 'Code sent', response.ok ? 'success' : 'error');
    } catch {
      showToast('Failed to resend code', 'error');
    } finally {
      setIsResending(false);
    }
  };

  const triggerButton = children ? (
    children
  ) : variant === 'hero' ? (
      <button
        type="button"
        className={cn(
          'flex w-full items-center justify-between gap-2',
          'mt-3 rounded-2xl border-[1px] border-[#cee0f4] bg-sky-50 px-3 py-3',
          'shadow-lg',
          'hover:bg-periwinkle/60 transition-colors',
          'sm:gap-3 sm:px-9',
          triggerClassName
        )}
      >
        <span aria-hidden="true">
          <MessageSquareText className="text-primary size-6 sm:size-8" />
        </span>
        <span className="font-figtree text-primary flex-1 text-center text-sm font-bold tracking-wide sm:text-base lg:text-lg">
          {triggerLabel}
        </span>
        <ArrowRight className="text-primary size-8 shrink-0 font-bold" />
      </button>
    ) : (
      <Button
        variant="outline"
        size="sm"
        className={cn(
          'border-dark-blue text-dark-blue hover:bg-periwinkle hover:text-dark-blue',
          'h-auto w-auto max-w-full rounded-full px-4 py-3 text-left text-sm break-words whitespace-normal',
          triggerClassName
        )}
      >
        {triggerLabel}
      </Button>
    );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{triggerButton}</DialogTrigger>

      <DialogContent className="border-primary/20 gap-0 overflow-hidden p-0 sm:max-w-md">
        <DialogHeader className="border-b border-gray-100 px-6 pt-6 pb-4">
          <DialogTitle className="text-primary flex items-center gap-2 text-xl">
            <Sparkles className="size-5 shrink-0" />
            Daily Scripture
          </DialogTitle>
          <DialogDescription className="text-muted-foreground text-left text-sm">
            Get a daily Scripture video inspiration by text message.
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 pt-4 pb-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="border-dark-blue mb-4 grid h-auto w-full grid-cols-2 rounded-full border bg-white p-1">
              <TabsTrigger
                value="subscribe"
                className={cn(
                  'text-dark-blue data-[state=active]:bg-primary rounded-full data-[state=active]:text-white',
                  'hover:bg-periwinkle data-[state=active]:hover:bg-primary'
                )}
              >
                Subscribe
              </TabsTrigger>
              <TabsTrigger
                value="unsubscribe"
                className={cn(
                  'text-dark-blue data-[state=active]:bg-primary rounded-full data-[state=active]:text-white',
                  'hover:bg-periwinkle data-[state=active]:hover:bg-primary'
                )}
              >
                Unsubscribe
              </TabsTrigger>
            </TabsList>

            <TabsContent value="subscribe" className="mt-0">
              {subscribeStep === 'form' && (
                <>
                  <p className="text-muted-foreground text-sm">
                    Enter your details and verify your phone with a one-time code to start receiving
                    daily Scripture inspiration.
                  </p>

                  <div className="pt-4">
                    <Form {...subscribeForm}>
                      <form
                        onSubmit={subscribeForm.handleSubmit(onSubscribe)}
                        className="space-y-4"
                      >
                        <FormField
                          control={subscribeForm.control}
                          name="name"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Name</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="Your name"
                                  className="h-10 rounded-lg bg-white"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={subscribeForm.control}
                          name="phoneNumber"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Phone Number</FormLabel>
                              <FormControl>
                                <PhoneInput
                                  country="us"
                                  onlyCountries={['us']}
                                  value={field.value}
                                  disableDropdown={true}
                                  countryCodeEditable={false}
                                  onChange={(value, country) => {
                                    field.onChange(value.startsWith('+') ? value : `+${value}`);
                                    subscribeForm.setValue('countryCode', country.dialCode);
                                  }}
                                  inputClass="!w-full !h-10 !text-sm !rounded-lg !ml-[0px]"
                                  containerClass="!w-full"
                                  buttonClass="!border-input !rounded-l-lg"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={subscribeForm.control}
                          name="timezone"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Timezone</FormLabel>
                              <Select onValueChange={field.onChange} value={field.value}>
                                <FormControl>
                                  <SelectTrigger className="focus-visible:ring-dark-blue !h-10 w-full rounded-lg bg-white">
                                    <SelectValue placeholder="Select timezone" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent className="max-h-60 bg-white">
                                  {timezoneOptions.map((tz) => (
                                    <SelectItem key={tz} value={tz}>
                                      {tz.replace(/_/g, ' ')}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={subscribeForm.control}
                          name="smsConsent"
                          render={({ field }) => (
                            <FormItem className="flex flex-row items-start gap-3 space-y-0">
                              <FormControl>
                                <Checkbox
                                  checked={field.value}
                                  onCheckedChange={field.onChange}
                                  className="mt-0.5"
                                />
                              </FormControl>
                              <div className="space-y-1 leading-none">
                                <FormLabel className="text-muted-foreground cursor-pointer text-xs font-normal">
                                  I agree to receive daily Scripture text messages from SmartGuy.
                                  Message and data rates may apply.
                                </FormLabel>
                                <FormMessage />
                              </div>
                            </FormItem>
                          )}
                        />

                        <div className="flex justify-end pt-1">
                          <Button
                            type="submit"
                            variant="default"
                            className="bg-primary hover:bg-dark-blue rounded-full"
                            disabled={subscribeForm.formState.isSubmitting}
                          >
                            Continue
                            {subscribeForm.formState.isSubmitting && (
                              <Loader2 className="ml-2 size-5 animate-spin" />
                            )}
                          </Button>
                        </div>
                      </form>
                    </Form>
                  </div>
                </>
              )}

              {subscribeStep === 'verify' && (
                <>
                  <p className="text-sm">
                    Enter the 6-digit code sent to{' '}
                    <span className="text-primary font-medium">
                      +{phonePayload.countryCode} {phonePayload.phoneNumber}
                    </span>
                    .
                  </p>

                  <div className="pt-4">
                    <Form {...verifyForm}>
                      <form onSubmit={verifyForm.handleSubmit(onVerify)} className="space-y-4">
                        <FormField
                          control={verifyForm.control}
                          name="code"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Verification Code</FormLabel>
                              <FormControl>
                                <InputOTP
                                  maxLength={6}
                                  pattern={REGEXP_ONLY_DIGITS}
                                  value={field.value}
                                  onChange={field.onChange}
                                >
                                  <InputOTPGroup>
                                    <InputOTPSlot index={0} />
                                    <InputOTPSlot index={1} />
                                    <InputOTPSlot index={2} />
                                    <InputOTPSlot index={3} />
                                    <InputOTPSlot index={4} />
                                    <InputOTPSlot index={5} />
                                  </InputOTPGroup>
                                </InputOTP>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <div className="flex items-center justify-between">
                          <Button
                            type="button"
                            variant="link"
                            className="text-primary h-auto p-0"
                            disabled={isResending}
                            onClick={handleResendOtp}
                          >
                            {isResending ? 'Sending...' : 'Resend code'}
                          </Button>

                          <Button
                            type="submit"
                            variant="default"
                            className="bg-primary hover:bg-dark-blue rounded-full"
                            disabled={verifyForm.formState.isSubmitting}
                          >
                            Verify
                            {verifyForm.formState.isSubmitting && (
                              <Loader2 className="ml-2 size-5 animate-spin" />
                            )}
                          </Button>
                        </div>
                      </form>
                    </Form>
                  </div>
                </>
              )}

              {subscribeStep === 'success' && (
                <div className="flex flex-col items-center space-y-4 py-4 text-center">
                  <div className="bg-periwinkle flex size-14 items-center justify-center rounded-full">
                    <CheckCircle2 className="text-primary size-8" />
                  </div>
                  <p className="text-sm">
                    You&apos;re all set! You&apos;ll receive daily Scripture inspiration messages on
                    your phone.
                  </p>
                  <Button
                    variant="default"
                    className="bg-primary hover:bg-dark-blue rounded-full"
                    onClick={() => handleOpenChange(false)}
                  >
                    Done
                  </Button>
                </div>
              )}
            </TabsContent>

            <TabsContent value="unsubscribe" className="mt-0">
              <div className="space-y-4 py-1">
                <p className="text-sm">
                  To unsubscribe from daily Scripture texts, tap the <strong>Unsubscribe</strong>{' '}
                  link included in any inspirational SMS you&apos;ve received from SmartGuy.
                </p>
                <p className="text-muted-foreground text-sm">
                  Each message includes a personal unsubscribe link that works immediately — no
                  login required.
                </p>
                <p className="text-muted-foreground text-sm">
                  If you no longer have the message, wait for your next daily SMS to use the
                  unsubscribe link.
                </p>
                <div className="flex justify-end">
                  <Button
                    variant="outline"
                    className="border-dark-blue text-dark-blue hover:bg-periwinkle hover:text-dark-blue rounded-full"
                    onClick={() => handleOpenChange(false)}
                  >
                    Close
                  </Button>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
}
